/** Combinatorics and hypergeometric odds for an 80-ball / 20-drawn keno game. */

export const KENO_BALLS = 80;
export const KENO_DRAWN = 20;

const combCache = new Map<string, number>();

/** n choose k, memoised, computed multiplicatively to stay exact in doubles for n<=80. */
export function choose(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;
  const key = `${n},${k}`;
  const hit = combCache.get(key);
  if (hit !== undefined) return hit;
  const kk = Math.min(k, n - k);
  let r = 1;
  for (let i = 1; i <= kk; i++) r = (r * (n - kk + i)) / i;
  r = Math.round(r);
  combCache.set(key, r);
  return r;
}

/** P(exactly `hits` of `spots` picked numbers are among the 20 drawn from 80). */
export function hitProbability(spots: number, hits: number, balls = KENO_BALLS, drawn = KENO_DRAWN): number {
  if (hits < 0 || hits > spots || hits > drawn) return 0;
  return (choose(drawn, hits) * choose(balls - drawn, spots - hits)) / choose(balls, spots);
}

/** Full distribution for a spot count, index = hits. */
export function hitDistribution(spots: number): number[] {
  const out: number[] = [];
  for (let h = 0; h <= spots; h++) out.push(hitProbability(spots, h));
  return out;
}

/**
 * P(k of the 20 drawn numbers fall in 1..40) -- the Heads/Tails split.
 * Same hypergeometric: 20 drawn from 80 where 40 are "heads".
 */
export function headsCountProbability(k: number): number {
  return (choose(40, k) * choose(40, KENO_DRAWN - k)) / choose(KENO_BALLS, KENO_DRAWN);
}

/** Expected return per 1 unit staked for a paytable (index = hits, value = multiplier of stake). */
export function expectedReturn(spots: number, paytable: readonly number[]): number {
  let rtp = 0;
  for (let h = 0; h <= spots; h++) rtp += hitProbability(spots, h) * (paytable[h] ?? 0);
  return rtp;
}

export function sideProbability(): number {
  let p = 0;
  for (let k = 11; k <= 20; k++) p += headsCountProbability(k);
  return p;
}

export function evensProbability(): number {
  return headsCountProbability(10);
}
